// Copyright (c) 2016 Deere & Company

import './labor-rate-history-report.scss';
import React, {
    Component,
    PropTypes
} from 'react';
import FavoriteIcon from '../../../shared/components/favorites/favorite-icon';
import {FormattedMessage} from 'react-intl';
import LaborRateHistory from '@deere/cps-it.warranty-model-factory/lib/labor-rate-history';
import LaborRateHistoryReportCriteria from './labor-rate-history-report-criteria';
import NumberOfRecordsFound from '../../../shared/components/number-of-records-found/index';
import ReportGrid from '../../shared/components/report-grid';
import ReportHeader from '../../shared/components/report-header';
import ReportHiddenColumns from '../../shared/components/report-columns';
import ReportLayout from '@deere/cps-it.warranty-model-factory/lib/report-layout';
import Security from '@deere/cps-it.warranty-model-factory/lib/security';
import ShowNoResultsFound from '../../shared/components/show-no-results-found/show-no-results-found';
import WarrantyAgGridDatePicker from '../../shared/components/ag-grid-date-picker';
import WarrantyLoadingIndicator from '../../../shared/components/warranty-loading-indicator';
import {exportToCSV} from '../../../reports/shared/services/export-to-csv';
import {getAgGridLocaleText} from '../../../shared/utils/ag-grid-locale-text';
import {getDefaultReportLayout} from '../../shared/services/report-variant';
import {getLaborRateHistoryColumnDefinitions} from '../report-services/column-definitions';
import {isHistoryToDisplayInRange} from '../services/history-to-display-range-validity';
import {scrollToAndResizeReport} from '../../shared/services/scroll-window';

function handleHistoryToDisplayChange(event) {

    if (event.target.value) {

        const historyToDisplay = parseInt(event.target.value, 10);

        if (isHistoryToDisplayInRange(historyToDisplay)) {

            this.actions.updateLaborRateHistoryCriteria(historyToDisplay);

        }

    } else {

        this.actions.resetLaborRateHistoryCriteria();

    }

}

function handleCreateLaborRateHistoryReport() {

    this.actions.toggleReportIsScrollable();
    this.actions.createLaborRateHistoryReport();

}

function handleGridApiDefinition(params) {

    let defaultColumnState,
        defaultSortModel;

    this.actions.createAgGridApi(params.api, params.columnApi);

    if (this.selectedLayout && this.selectedLayout.name && !this.isInitialLoad) {

        const defaultLayout = this.layouts.find(layout => !layout.name);

        defaultColumnState = defaultLayout.columnState;
        defaultSortModel = defaultLayout.sortModel;

    } else {

        defaultColumnState = params.columnApi.getColumnState();
        defaultSortModel = params.api.getSortModel();

        this.actions.updateReportProperty('isInitialLoad', false);

    }

    this.actions.updateDefaultLayout(defaultColumnState, defaultSortModel);

    if (this.selectedLayout && this.selectedLayout.name) {

        params.columnApi.setColumnState(this.selectedLayout.columnState);
        params.api.setSortModel(this.selectedLayout.sortModel);
        params.api.setFilterModel(this.selectedLayout.filterModel);

    } else {

        const defaultLayout = getDefaultReportLayout(defaultColumnState, defaultSortModel);

        this.actions.updateReportProperty('selectedLayout', defaultLayout);

    }

}

function onColumnRemoved(columnEvent) {

    if (!columnEvent.visible) {

        this.actions.hideReportColumn(this.columnApi.getColumnState());

    }

}

function handleExportToCsv() {

    const fileName = 'labor-rate-history';

    return exportToCSV(fileName, this.gridApi);

}

function handleCriteriaReset() {

    this.actions.updateLaborRateHistoryCriteria(90);
    this.actions.resetLaborRateHistoryReport();
    this.actions.updateShowNoResultsFound(false);

}

class LaborRateHistoryReport extends Component {

    componentWillMount() {

        this.props.actions.getReportLayouts('labor-rate-history');

    }

    componentDidUpdate(prevProps) {

        if (this.props.reportIsScrollable && this.props.report.length) {

            this.props.actions.resetReportIsScrollable();

            scrollToAndResizeReport();

        }

        const isFirstRun = !prevProps.report.length && !prevProps.showNoResultsFound;
        const layoutWasChanged = this.props.selectedLayout !== prevProps.selectedLayout;
        const readyToApplyLayout = this.props.selectedLayout && Object.keys(this.props.columnApi).length && this.props.isDefaultColumnStateAvailable;

        if ((isFirstRun || layoutWasChanged) && readyToApplyLayout) {

            this.props.columnApi.setColumnState(this.props.selectedLayout.columnState);
            this.props.gridApi.setSortModel(this.props.selectedLayout.sortModel);
            this.props.gridApi.setFilterModel(this.props.selectedLayout.filterModel);

        }

    }

    componentWillUnmount() {

        this.props.actions.resetLaborRateHistoryReport();
        this.props.actions.updateLaborRateHistoryCriteria(90);
        this.props.actions.updateShowNoResultsFound(false);
        this.props.actions.resetReportVariant();

    }

    render() {

        const canExportToExcel = this.props.report.length > 0;

        const canRunReport = this.props.historyToDisplay > 0 && this.props.historyToDisplay < 10000;

        const gridOptions = {
            dateComponentFramework: WarrantyAgGridDatePicker
        };

        return (
            <section className='labor-rate-history'>
                <WarrantyLoadingIndicator isLoading={this.props.busyCounter > 0}/>
                <h2 className='labor-rate-history-title'>
                    <FormattedMessage id='LABOR_RATE_HISTORY'/>
                    <FavoriteIcon
                        id='LABOR_RATE_HISTORY'
                        route={this.props.pathName}
                    />
                </h2>
                <LaborRateHistoryReportCriteria
                    canExport={canExportToExcel}
                    canRunReport={canRunReport}
                    historyToDisplay={this.props.historyToDisplay}
                    onCreateLaborRateHistoryReport={this.props::handleCreateLaborRateHistoryReport}
                    onCriteriaReset={this.props::handleCriteriaReset}
                    onExportToCsv={this.props::handleExportToCsv}
                    onHistoryToDisplayChange={this.props::handleHistoryToDisplayChange}
                />
                {this.props.report.length ?
                    <div>
                        <NumberOfRecordsFound count={this.props.report.length}/>
                        <section className='report'>
                            {this.props.isReportColumnsVisible && <ReportHiddenColumns/>}
                            <ReportHeader
                                columnApi={this.props.columnApi}
                                gridApi={this.props.gridApi}
                                reportName='labor-rate-history'
                            />
                            <ReportGrid
                                columnDefs={getLaborRateHistoryColumnDefinitions(this.context.security)}
                                gridOptions={gridOptions}
                                localeText={getAgGridLocaleText()}
                                onColumnVisible={this.props::onColumnRemoved}
                                onGridReady={this.props::handleGridApiDefinition}
                                rowData={this.props.report}
                            />
                        </section>
                    </div> :
                    <ShowNoResultsFound showNoResultsFound={this.props.showNoResultsFound}/>
                }
            </section>
        );

    }

}

LaborRateHistoryReport.displayName = 'LaborRateHistoryReport';
LaborRateHistoryReport.propTypes = {
    actions: PropTypes.object.isRequired,
    busyCounter: PropTypes.number.isRequired,
    columnApi: PropTypes.object.isRequired,
    gridApi: PropTypes.object.isRequired,
    historyToDisplay: PropTypes.number,
    isDefaultColumnStateAvailable: PropTypes.bool.isRequired,
    isInitialLoad: PropTypes.bool.isRequired,
    isReportColumnsVisible: PropTypes.bool.isRequired,
    layouts: PropTypes.arrayOf(PropTypes.instanceOf(ReportLayout)).isRequired,
    pathName: PropTypes.string.isRequired,
    report: PropTypes.arrayOf(PropTypes.instanceOf(LaborRateHistory)),
    reportIsScrollable: PropTypes.bool.isRequired,
    selectedLayout: PropTypes.instanceOf(ReportLayout),
    showNoResultsFound: PropTypes.bool.isRequired
};
LaborRateHistoryReport.contextTypes = {
    security: PropTypes.instanceOf(Security).isRequired
};

export default LaborRateHistoryReport;



// WEBPACK FOOTER //
// ./src/reports/labor-rate-history/components/labor-rate-history-report.js